import React, { useState } from "react";
import { useSelector } from "react-redux";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import Popover from "@mui/material/Popover";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CourtGridTimeSlot, CourtGridCourt } from "../../../../models/CourtGridData";
import { GridHeader } from "../../../shared/GridHeader";
import { TimeSlot, TimeSlotData, SlotStatus } from "../../../shared/TimeSlot";
import EAIconButton from "../../../shared/Buttons/EAIconButton";
import useCourtGridTable from "./useCourtGridTable";
import { SaleStep } from "../../../../store/stepSaleConfig/stepSaleConfigReducer";
import { State } from "../../../../store";
import "./style.scss";
import EAButton from "../../../shared/Buttons/EAButton";

interface IProps {
  displayedCourts: CourtGridCourt[];
  filteredCourts?: CourtGridCourt[];
  visibleHours: string[];
  hasPastHours: boolean;
  findSlot: (courtName: string, startTime: string) => CourtGridTimeSlot | undefined;
  getSlotStatus: (timeSlot: CourtGridTimeSlot) => SlotStatus;
  onSlotClick: (timeSlot: CourtGridTimeSlot, courtName: string) => void;
  onShowPastHours: () => void;
  onCourtSelect?: (index: number) => void;
  // Mobile navigation props
  onPreviousCourt?: () => void;
  onNextCourt?: () => void;
  currentCourtIndex?: number;
  filteredCourtsLength?: number;
  saleStep?: SaleStep;
}

const CourtGridTable: React.FC<IProps> = ({
  displayedCourts,
  filteredCourts = [],
  visibleHours,
  hasPastHours,
  findSlot,
  getSlotStatus,
  onSlotClick,
  onShowPastHours,
  onCourtSelect,
  onPreviousCourt,
  onNextCourt,
  currentCourtIndex = 0,
  filteredCourtsLength = 0,
  saleStep,
}) => {
  const showTimeOfEntance = useSelector((state: State) => state.config.configuration.showTimeOfEntance);
  const showTicketsPoolForStepByStepSale = useSelector(
    (state: State) => state.config.configuration.showTicketsPoolForStepByStepSale,
  );
  const [courtPickerAnchor, setCourtPickerAnchor] = useState<HTMLElement | null>(null);

  const { getGridTemplateColumns } = useCourtGridTable({
    displayedCourts,
    visibleHours,
    hasPastHours,
  });

  const isMobile = useMediaQuery("(max-width: 980px)");

  if (displayedCourts.length === 0) {
    return (
      <div className="court-grid-table-empty">
        <p>Brak dostępnych kortów dla wybranych filtrów.</p>
      </div>
    );
  }

  const handleSlotClick = (data: TimeSlotData, contextName: string) => {
    const slot = findSlot(contextName, data.startTime);
    if (slot) {
      onSlotClick(slot, contextName);
    }
  };

  const canGoPrevious = currentCourtIndex > 0;
  const canGoNext = currentCourtIndex < filteredCourtsLength - 1;

  return (
    <div className="court-grid-table">
      <div
        className={`court-grid-table__header${isMobile && filteredCourtsLength > 1 ? " court-grid-table__header--mobile-nav" : ""}`}
        style={isMobile && filteredCourtsLength > 1 ? undefined : { gridTemplateColumns: getGridTemplateColumns() }}
      >
        {(!isMobile || filteredCourtsLength <= 1) && <div className="court-grid-table__time-header">Godzina</div>}
        {displayedCourts.map((court) => (
          <div key={court.courtName} className="court-grid-table__court-header">
            {isMobile && filteredCourtsLength > 1 ? (
              <div className="court-grid-table__mobile-court-nav">
                <EAIconButton
                  title="Poprzedni kort"
                  icon={<MdChevronLeft />}
                  variant="icon"
                  onClick={onPreviousCourt}
                  disabled={!canGoPrevious}
                />
                <div
                  className={`court-grid-table__court-nav-content${filteredCourtsLength > 1 ? " court-grid-table__court-nav-content--clickable" : ""}`}
                  onClick={filteredCourtsLength > 1 ? (e) => setCourtPickerAnchor(e.currentTarget) : undefined}
                >
                  <span className="court-grid-table__court-name">{court.courtName}</span>
                  <span className="court-grid-table__court-counter">
                    {currentCourtIndex + 1}/{filteredCourtsLength}
                  </span>
                </div>
                <EAIconButton
                  title="Następny kort"
                  icon={<MdChevronRight />}
                  variant="icon"
                  onClick={onNextCourt}
                  disabled={!canGoNext}
                />
              </div>
            ) : (
              <GridHeader title={court.courtName} description={court.description} imageUrl={court.imageUrl} />
            )}
          </div>
        ))}
      </div>

      <div className="court-grid-table__separator"></div>

      {hasPastHours && (
        <div className="court-grid-table__past-hours-row" style={{ gridTemplateColumns: getGridTemplateColumns() }}>
          <div
            className="court-grid-table__past-hours-cell"
            style={{ gridColumn: `1 / ${displayedCourts.length + 2}` }}
          >
            <EAButton
              title="Pokaż wcześniejsze godziny"
              onClick={onShowPastHours}
              variant="caption"
              color="dark"
              compact
            />
          </div>
        </div>
      )}

      <Popover
        open={Boolean(courtPickerAnchor)}
        anchorEl={courtPickerAnchor}
        onClose={() => setCourtPickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <ul className="court-grid-table__court-picker">
          {filteredCourts.map((c, index) => (
            <li
              key={c.courtName}
              className={`court-grid-table__court-picker-item${index === currentCourtIndex ? " court-grid-table__court-picker-item--active" : ""}`}
              onClick={() => {
                onCourtSelect?.(index);
                setCourtPickerAnchor(null);
              }}
            >
              {c.courtName}
            </li>
          ))}
        </ul>
      </Popover>

      <div className="court-grid-table__body">
        {visibleHours.map((hour, hourIndex) => (
          <div key={hour} className="court-grid-table__row" style={{ gridTemplateColumns: getGridTemplateColumns() }}>
            <div className="court-grid-table__time-cell">
              <p className="court-grid-table__time-text">{hour}</p>
            </div>
            {displayedCourts.map((court) => {
              const slot = findSlot(court.courtName, hour);

              return (
                <div key={`${court.courtName}-${hour}`} className="court-grid-table__slot-cell">
                  {slot ? (
                    <TimeSlot
                      data={{
                        id: slot.exerciseId,
                        startTime: slot.startTime,
                        endTime: slot.endTime,
                        capacity: slot.capacity,
                        reserved: slot.reserved,
                        status: slot.status,
                        userAssigned: slot.userAssigned,
                        saleTo: slot.internetSaleTo,
                      }}
                      contextName={court.courtName}
                      status={getSlotStatus(slot)}
                      onClick={handleSlotClick}
                      isFirstRow={hourIndex === 0}
                      showOccupancy={saleStep?.showOtherTicketsPool ?? false}
                      showTimeOfEntance={showTimeOfEntance}
                      showTicketsPoolForStepByStepSale={showTicketsPoolForStepByStepSale}
                    />
                  ) : (
                    <TimeSlot
                      data={{
                        id: 0,
                        startTime: hour,
                        endTime: hour,
                        capacity: 0,
                        reserved: 0,
                      }}
                      contextName={court.courtName}
                      status="unavailable"
                      isFirstRow={hourIndex === 0}
                      showOccupancy={saleStep?.showOtherTicketsPool ?? false}
                      showTimeOfEntance={showTimeOfEntance}
                      showTicketsPoolForStepByStepSale={showTicketsPoolForStepByStepSale}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CourtGridTable;
